import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  SnackbarCloseReason,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import * as yup from 'yup';
import { useAuthContext } from '../../contexts';
import { useBloqueioSalvamento } from '../../hooks';
import { TelaCarregando } from '../feedback/TelaCarregando';
import { ConstellationBackground } from './ConstellationBackground';
import { marcarLoginParaAvisoCertificado } from '../certificado/AvisoCertificado';

const loginSchema = yup.object().shape({
  usuario: yup.string().required('Usuário é obrigatório'),
  senha: yup.string().required('Senha é obrigatória').min(5, 'Mínimo 5 caracteres'),
});

const REMEMBER_ME_STORAGE_KEY = 'APP_REMEMBERED_USER';

// Paleta da tela de login — espelha exatamente o PDVSIAC
// (instrucoes_login_siac_css.txt, "TELA DE LOGIN SIAC 100% CSS"). Visual
// único, não acompanha o tema claro/escuro do resto do sistema.
const SIAC = {
  dark: '#062D3A',
  cyan: '#42F2F7',
  cyanHover: '#1FDDE3',
  cyanIcon: '#0B7C86',
  bgLight: '#F7FAFC',
  text: '#062D3A',
  muted: '#687E8A',
  labelText: '#314954',
  border: '#D9E4E8',
};

// Breakpoints próprios do spec (1100/850/480) — não batem com os do MUI
// (900/1200/...), então usados como media queries literais, na frente dos
// valores "base" (sempre aplicados) pra sobrescrever só abaixo de cada
// limite. Ordem no objeto importa: os mais estreitos vêm por último, pra
// ganhar quando mais de uma faixa bate ao mesmo tempo.
const MQ_1100 = '@media (max-width:1100px)';
const MQ_850 = '@media (max-width:850px)';
const MQ_480 = '@media (max-width:480px)';

// Campos (Usuário/Senha) — ícone dentro do próprio campo (cinza-ciano
// discreto), sem bloco colorido lateral. Altura/raio/sombra batem com o
// spec (--siac-radius-input:13px, --siac-shadow-input).
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 58,
    borderRadius: '13px',
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
    fontSize: '15px',
    color: SIAC.text,
    boxShadow: '0 2px 5px rgba(0, 30, 39, 0.035), 0 10px 22px rgba(0, 30, 39, 0.035)',
    transition: 'border-color 180ms ease, box-shadow 180ms ease, background 180ms ease',
    '& fieldset': { borderColor: SIAC.border },
    '&:hover fieldset': { borderColor: '#C7D8DE' },
    '&.Mui-focused': { backgroundColor: '#FFFFFF' },
    '&.Mui-focused fieldset': { borderColor: SIAC.cyan, borderWidth: '1.5px' },
    [MQ_480]: { height: 55 },
  },
  '& .MuiFormHelperText-root': { fontSize: '0.78rem', mx: 0.25 },
};

interface ILoginProps {
  children: React.ReactNode;
}

// Tela cheia mostrada no lugar do sistema quando a empresa do usuário está desativada no
// painel de controle (ver AuthContext.tsx > empresaInativa). Reaproveita a paleta escura
// do painel esquerdo do login em vez do tema claro/escuro do resto do sistema — mesma
// lógica de "essa tela é sempre a mesma, não acompanha o tema".
const TelaEmpresaDesativada: React.FC<{ onVoltar: () => void }> = ({ onVoltar }) => (
  <Box
    sx={{
      width: '100%',
      height: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '18px',
      textAlign: 'center',
      px: 3,
      background: 'linear-gradient(145deg, #0B4F60 0%, #083D4A 38%, #062D3A 72%, #041F29 100%)',
    }}
  >
    <SupportAgentRoundedIcon sx={{ fontSize: 56, color: SIAC.cyan }} />
    <Typography sx={{ color: '#FFFFFF', fontSize: '24px', fontWeight: 750, letterSpacing: '-0.02em' }}>
      Empresa desativada
    </Typography>
    <Typography sx={{ color: 'rgba(255, 255, 255, 0.66)', fontSize: '15px', maxWidth: 380, lineHeight: 1.6 }}>
      O acesso dessa empresa ao sistema está temporariamente desativado. Consulte o suporte para regularizar e voltar a usar o SIAC.
    </Typography>
    <Button
      variant="outlined"
      onClick={onVoltar}
      sx={{
        mt: '12px',
        borderRadius: '12px',
        textTransform: 'none',
        color: SIAC.cyan,
        borderColor: 'rgba(66, 242, 247, 0.4)',
        '&:hover': { borderColor: SIAC.cyan, background: 'rgba(66, 242, 247, 0.08)' },
      }}
    >
      Voltar para o login
    </Button>
  </Box>
);

export const Login: React.FC<ILoginProps> = ({ children }) => {
  const { isAuthenticated, empresaInativa, login, logout } = useAuthContext();

  // Fases do pós-login: 'form' (tela normal) -> 'loadingSystem' (autenticado, mas ainda
  // segurando a tela preta por um tempo mínimo para o sistema carregar atrás dela) ->
  // 'app' (tela preta já sumiu, só o sistema fica). Sem isso, isAuthenticated virava true
  // e a troca para {children} era instantânea, sem transição e sem esperar nada carregar.
  const [phase, setPhase] = useState<'form' | 'loadingSystem' | 'app'>('form');
  const [fadingOverlay, setFadingOverlay] = useState(false);
  // isAuthenticated também vira true num F5 comum (o AuthContext acha o token já salvo
  // no localStorage) — sem essa flag, a tela de "Carregando sistema..." aparecia em
  // TODO refresh, não só logo depois de submeter o formulário de login.
  const justLoggedInRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const { isSaving, iniciar: iniciarLogin, concluir: concluirLogin, cancelar: cancelarLogin } = useBloqueioSalvamento();
  const [senhaError, setSenhaError] = useState('');
  const [usuarioError, setUsuarioError] = useState('');
  const [senha, setSenha] = useState('');
  const [usuario, setUsuario] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const senhaInputRef = useRef<HTMLInputElement>(null);
  // Celular (mesmo limite de 600px do AppMobile): layout próprio de app —
  // topo escuro com a marca + formulário num cartão que sobe por cima.
  const celular = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    const rememberedUser = localStorage.getItem(REMEMBER_ME_STORAGE_KEY);
    if (rememberedUser) {
      setUsuario(rememberedUser);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && phase === 'form') {
      setPhase(justLoggedInRef.current ? 'loadingSystem' : 'app');
      justLoggedInRef.current = false;
    } else if (!isAuthenticated && phase !== 'form') {
      setPhase('form');
      setFadingOverlay(false);
    }
  }, [isAuthenticated, phase]);

  useEffect(() => {
    if (phase !== 'loadingSystem') return;

    let fadeTimeout: ReturnType<typeof setTimeout>;
    const minWaitTimeout = setTimeout(() => {
      setFadingOverlay(true);
      fadeTimeout = setTimeout(() => setPhase('app'), 1100);
    }, 1100);

    return () => {
      clearTimeout(minWaitTimeout);
      clearTimeout(fadeTimeout);
    };
  }, [phase]);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const handleSubmit = () => {
    if (!iniciarLogin()) return;

    setIsLoading(true);
    setSenhaError('');
    setUsuarioError('');

    loginSchema
      .validate({ usuario: usuario.trim(), senha: senha.trim() }, { abortEarly: false })
      .then(dadosValidados => {
        login(dadosValidados.usuario, dadosValidados.senha)
          .then(() => {
            justLoggedInRef.current = true;
            // Aviso de certificado digital vencendo/vencido: a cada login (não a cada F5).
            marcarLoginParaAvisoCertificado();
            // Depois de entrar, abre sempre no painel inicial. O BrowserRouter
            // ainda não está montado (ele só existe dentro de {children}),
            // então ajustar a URL aqui já basta pra ele nascer em /inicio.
            window.history.replaceState(null, '', '/inicio');
            if (rememberMe) {
              localStorage.setItem(REMEMBER_ME_STORAGE_KEY, dadosValidados.usuario);
            } else {
              localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
            }
            setIsLoading(false);
            concluirLogin();
          })
          .catch((error) => {
            setIsLoading(false);
            cancelarLogin();
            setTms('error');
            setMsg(error?.message || 'Usuário ou senha incorretos');
            setOpenmsg(true);
          });
      })
      .catch((errors: yup.ValidationError) => {
        setIsLoading(false);
        cancelarLogin();
        errors.inner.forEach(error => {
          if (error.path === 'usuario') setUsuarioError(error.message);
          if (error.path === 'senha') setSenhaError(error.message);
        });
      });
  };

  // Formulário (título, campos, lembrar-me, Entrar, aviso de segurança) — o
  // mesmo no computador (lado direito do grid) e no celular (cartão que sobe
  // por cima do topo escuro, ver layout `celular` mais abaixo).
  const formulario = (
    <>
          <Typography
            sx={{
              m: 0,
              color: SIAC.text,
              fontSize: 'clamp(27px, 2.2vw, 31px)',
              lineHeight: 1.15,
              fontWeight: 750,
              letterSpacing: '-0.035em',
              [MQ_480]: { fontSize: '27px' },
            }}
          >
            Acesse sua conta
          </Typography>
          <Typography sx={{ mt: '8px', mb: 'clamp(14px, 2.5vh, 26px)', color: SIAC.muted, fontSize: '15px' }}>
            Informe suas credenciais para entrar
          </Typography>

          <Box sx={{ mb: 'clamp(12px, 2vh, 22px)' }}>
            <Typography component="label" sx={{ display: 'block', mb: '8px', color: SIAC.labelText, fontSize: '12px', fontWeight: 650 }}>
              Usuário
            </Typography>
            <TextField
              fullWidth
              // No celular não foca sozinho: o teclado abriria cobrindo metade
              // da tela antes mesmo do usuário ver o login.
              autoFocus={!celular}
              variant="outlined"
              placeholder="Usuário"
              hiddenLabel
              value={usuario}
              disabled={isLoading || isSaving}
              error={!!usuarioError}
              helperText={usuarioError}
              onKeyDown={e => {
                setUsuarioError('');
                if (e.key === 'Enter') {
                  e.preventDefault();
                  senhaInputRef.current?.focus();
                }
              }}
              onChange={e => setUsuario(e.target.value)}
              // Teclado do celular: sem maiúscula/corretor automáticos no
              // usuário, "próximo" no lugar do Enter, e preenchimento
              // automático do gerenciador de senhas (iCloud/Google).
              inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false, autoComplete: 'username', enterKeyHint: 'next' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineRoundedIcon sx={{ color: SIAC.cyanIcon, fontSize: 22 }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Box>

          <Box sx={{ mb: 'clamp(12px, 2vh, 22px)' }}>
            <Typography component="label" sx={{ display: 'block', mb: '8px', color: SIAC.labelText, fontSize: '12px', fontWeight: 650 }}>
              Senha
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Senha"
              hiddenLabel
              type={mostrarSenha ? 'text' : 'password'}
              value={senha}
              disabled={isLoading || isSaving}
              error={!!senhaError}
              helperText={senhaError}
              inputRef={senhaInputRef}
              onKeyDown={e => {
                setSenhaError('');
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              onChange={e => setSenha(e.target.value)}
              inputProps={{ autoComplete: 'current-password', enterKeyHint: 'go' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: SIAC.cyanIcon, fontSize: 21 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setMostrarSenha(old => !old)}
                      edge="end"
                      tabIndex={-1}
                      sx={{ color: SIAC.muted }}
                    >
                      {mostrarSenha ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={rememberMe}
                disabled={isLoading || isSaving}
                onChange={e => setRememberMe(e.target.checked)}
                sx={{
                  color: SIAC.border,
                  '& svg': { fontSize: 18 },
                  '&.Mui-checked': { color: SIAC.cyan },
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '13px', color: SIAC.text }}>
                Lembrar-me
              </Typography>
            }
            sx={{ ml: -0.5, mt: '3px', mb: 'clamp(14px, 2.2vh, 25px)' }}
          />

          <Button
            fullWidth
            variant="contained"
            disabled={isLoading || isSaving}
            onClick={handleSubmit}
            endIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardRoundedIcon />}
            sx={{
              height: 54,
              borderRadius: '12px',
              fontWeight: 700,
              fontSize: '15px',
              textTransform: 'none',
              background: 'linear-gradient(90deg, #42F2F7 0%, #42F2F7 55%, #42F2F7 100%)',
              color: SIAC.dark,
              boxShadow: '0 8px 24px rgba(66, 242, 247, 0.20)',
              transition: 'transform 150ms ease, box-shadow 150ms ease, filter 150ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                filter: 'brightness(.98)',
                boxShadow: '0 11px 26px rgba(66, 242, 247, 0.25)',
                background: 'linear-gradient(90deg, #42F2F7 0%, #42F2F7 55%, #42F2F7 100%)',
              },
              '&:active': { transform: 'translateY(0)' },
              [MQ_480]: { height: 52 },
            }}
          >
            Entrar
          </Button>

          {/* Informação de segurança — spec seção 13. */}
          <Box
            sx={{
              mt: 'clamp(20px, 6vh, 90px)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              maxWidth: 360,
              color: SIAC.muted,
              fontSize: '12px',
              lineHeight: 1.5,
              [MQ_850]: { mt: 'clamp(16px, 4vh, 60px)' },
            }}
          >
            <VerifiedUserOutlinedIcon sx={{ fontSize: 18, color: SIAC.cyanIcon, flexShrink: 0, mt: '1px' }} />
            <Typography sx={{ fontSize: 'inherit', color: 'inherit', lineHeight: 'inherit' }}>
              Seus dados estão protegidos com segurança de nível empresarial.
            </Typography>
          </Box>
    </>
  );

  // Prioridade sobre qualquer phase: mesmo que o login em si tenha "dado certo" (token
  // válido), empresa desativada nunca deixa entrar no sistema nem voltar pra tela de
  // formulário sozinha — só o botão "Voltar para o login" (logout) sai daqui.
  if (empresaInativa) return <TelaEmpresaDesativada onVoltar={logout} />;

  if (phase === 'app') return <>{children}</>;

  if (phase === 'loadingSystem') {
    return (
      <>
        {children}
        <TelaCarregando open message="Carregando sistema..." fading={fadingOverlay} />
      </>
    );
  }

  if (celular) {
    return (
      <Box
        sx={{
          width: '100%',
          // minHeight (não height): com o teclado aberto a tela precisa rolar
          // pra nenhum campo ficar escondido atrás dele.
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#FFFFFF',
          overflowX: 'hidden',
        }}
      >
        <TelaCarregando open={isSaving} message="Entrando..." />

        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openmsg}
          autoHideDuration={tms === 'error' ? null : 3000}
          onClose={handleCloseMsg}
          sx={{ top: 'calc(16px + env(safe-area-inset-top)) !important' }}
        >
          <Alert onClose={handleCloseMsg} severity={tms} variant="filled" sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>

        {/* Topo escuro com a marca — mesmo gradiente do painel esquerdo do
            computador, sem a animação de constelação (pesada pro celular). */}
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            color: '#FFFFFF',
            px: 3,
            pt: 'calc(28px + env(safe-area-inset-top))',
            // espaço extra embaixo: o cartão branco sobe por cima dele
            pb: '64px',
            minHeight: '34dvh',
            display: 'flex',
            flexDirection: 'column',
            background: `
              radial-gradient(circle at 80% 20%, rgba(66, 242, 247, 0.16) 0, rgba(66, 242, 247, 0.04) 35%, transparent 60%),
              linear-gradient(160deg, #0B4F60 0%, #083D4A 45%, #062D3A 100%)
            `,
            // Losangos decorativos (mesmas formas do lado claro do computador).
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 260,
              height: 260,
              right: -110,
              top: -90,
              transform: 'rotate(45deg)',
              border: '34px solid rgba(66, 242, 247, 0.07)',
              borderRadius: '48px',
              pointerEvents: 'none',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              width: 170,
              height: 170,
              left: -90,
              bottom: -40,
              transform: 'rotate(45deg)',
              border: '26px solid rgba(255, 255, 255, 0.04)',
              borderRadius: '36px',
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box
              component="img"
              src="/img/siac-logo-full.png"
              alt="SIAC"
              sx={{ width: 112, height: 'auto', filter: 'brightness(0) invert(1)' }}
            />
            <Typography sx={{ fontSize: '10px', letterSpacing: '0.22em', fontWeight: 600, color: 'rgba(255, 255, 255, 0.55)', mt: '4px' }}>
              ERP TORQ
            </Typography>
          </Box>

          <Box sx={{ position: 'relative', zIndex: 1, mt: 'auto', pt: 3 }}>
            <Typography sx={{ fontSize: '30px', lineHeight: 1.1, fontWeight: 800, letterSpacing: '-0.035em', color: '#FFFFFF' }}>
              Bem-vindo de{' '}
              <Box component="span" sx={{ color: SIAC.cyan }}>volta!</Box>
            </Typography>
            <Typography sx={{ mt: 1, fontSize: '14px', lineHeight: 1.5, color: 'rgba(255, 255, 255, 0.7)' }}>
              Acesse sua conta para continuar utilizando o sistema.
            </Typography>
          </Box>
        </Box>

        {/* Cartão do formulário — sobe por cima do topo, cantos arredondados
            (padrão "folha" dos apps de banco). */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            flex: 1,
            mt: '-36px',
            px: 2.5,
            pt: 3.5,
            pb: 'calc(28px + env(safe-area-inset-bottom))',
            bgcolor: SIAC.bgLight,
            borderTopLeftRadius: 28,
            borderTopRightRadius: 28,
            boxShadow: '0 -10px 30px rgba(0, 30, 39, 0.18)',
          }}
        >
          <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: SIAC.border, mx: 'auto', mt: '-14px', mb: 2.5 }} />
          {formulario}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        // height (não minHeight) — trava a página em 100dvh de verdade. Com
        // só minHeight, se o conteúdo de um dos painéis passasse da altura
        // da tela (telas baixas), a LINHA do grid crescia com ele e a
        // página toda ficava mais alta que a viewport, aparecendo scroll.
        // Os espaçamentos verticais dos painéis usam clamp(...,vh,...) —
        // ver abaixo — pra encolher em vez de estourar essa altura fixa.
        height: '100dvh',
        overflow: 'hidden',
        display: 'grid',
        // 38%/62% em telas normais, 42%/58% até 1100px, uma coluna só
        // (painel escuro escondido) até 850px — ver seção 1/16 do spec.
        gridTemplateColumns: '38% 62%',
        position: 'relative',
        bgcolor: SIAC.bgLight,
        [MQ_1100]: { gridTemplateColumns: '42% 58%' },
        [MQ_850]: { gridTemplateColumns: '1fr' },
      }}
    >
      <TelaCarregando open={isSaving} message="Entrando..." />

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={tms === 'error' ? null : 3000}
        onClose={handleCloseMsg}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      {/* PAINEL ESQUERDO — escondido abaixo de 850px (spec seção 16). Fundo
          100% CSS (gradiente + grade de pontos + formas, ver
          TechDecoration/LoginTechDecoration.tsx), sem nenhuma imagem. */}
      <Box
        sx={{
          display: 'block',
          position: 'relative',
          // Altura exata da linha do grid (já travada em 100dvh lá em cima)
          // — overflowX escondido segura o sangramento das formas
          // decorativas pras bordas; overflowY:auto é só uma rede de
          // segurança (não deveria disparar, já que o padding/conteúdo
          // aqui é enxuto o bastante pra caber em qualquer altura comum).
          height: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          color: '#FFFFFF',
          p: 'clamp(24px, 6vh, 54px) clamp(28px, 4vw, 48px) clamp(20px, 5vh, 42px)',
          background: `
            radial-gradient(circle at 35% 45%, rgba(66, 242, 247, 0.07) 0, rgba(66, 242, 247, 0.025) 30%, transparent 58%),
            linear-gradient(145deg, #0B4F60 0%, #083D4A 38%, #062D3A 72%, #041F29 100%)
          `,
          [MQ_1100]: { p: 'clamp(20px, 5vh, 42px) 34px' },
          [MQ_850]: { display: 'none' },
        }}
      >
        <ConstellationBackground />

        {/* Conteúdo real — acima da decoração (zIndex:2). */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* 1. Logo — largura em vez de altura (spec: width:150px,
              max-width:40%), preserva a proporção original via height:auto. */}
          <Box
            component="img"
            src="/img/siac-logo-full.png"
            alt="SIAC"
            sx={{ width: 'clamp(120px, 13vw, 150px)', maxWidth: '40%', height: 'auto', filter: 'brightness(0) invert(1)' }}
          />

          {/* 2. ERP TORQ */}
          <Typography
            sx={{
              mt: '16px',
              fontSize: '11px',
              letterSpacing: '0.22em',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.55)',
            }}
          >
            ERP TORQ
          </Typography>

          {/* 3. Bloco de boas-vindas — centralizado verticalmente no espaço
              sobrando (margin auto topo/baixo, como o spec pede). */}
          <Box sx={{ mt: 'auto', mb: 'auto', maxWidth: 370 }}>
            <Typography
              sx={{
                m: 0,
                fontSize: 'clamp(30px, 2.3vw, 42px)',
                lineHeight: 1.08,
                fontWeight: 750,
                letterSpacing: '-0.04em',
                // Sem isso, herdava o body1.color escuro do tema em vez do
                // branco do painel — mesma cor da logo.
                color: '#FFFFFF',
              }}
            >
              Bem-vindo de{' '}
              <Box component="span" sx={{ color: SIAC.cyan }}>
                volta!
              </Box>
            </Typography>
            <Typography
              sx={{
                mt: '16px',
                fontSize: '15px',
                lineHeight: 1.65,
                color: 'rgba(255, 255, 255, 0.66)',
              }}
            >
              Acesse sua conta para continuar utilizando o sistema.
            </Typography>
            {/* Traço ciano — só decorativo (spec seção 5). */}
            <Box sx={{ width: 78, height: 3, mt: '22px', borderRadius: 999, bgcolor: SIAC.cyan }} />
          </Box>
        </Box>
      </Box>

      {/* LADO DIREITO CLARO — fundo + formas 100% CSS (spec seções 6/7). */}
      <Box
        sx={{
          position: 'relative',
          // Mesma lógica do painel esquerdo — altura exata da linha do
          // grid, X escondido (pras formas decorativas que sangram pra
          // fora), Y com scroll só como rede de segurança.
          height: '100%',
          overflowX: 'hidden',
          overflowY: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 'clamp(20px, 5vh, 48px) 64px',
          background: `
            radial-gradient(circle at 76% 50%, rgba(66, 242, 247, 0.055), transparent 34%),
            linear-gradient(135deg, #FFFFFF 0%, #FAFCFD 55%, #F1F7F9 100%)
          `,
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 'clamp(480px, 37.5vw, 900px)',
            height: 'clamp(480px, 37.5vw, 900px)',
            right: 'clamp(-320px, -13vw, -180px)',
            top: 'clamp(-190px, -6.8vw, -90px)',
            transform: 'rotate(45deg)',
            border: 'clamp(30px, 2.8vw, 54px) solid rgba(10, 90, 116, 0.028)',
            borderRadius: 'clamp(50px, 4.7vw, 90px)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 'clamp(360px, 28vw, 675px)',
            height: 'clamp(360px, 28vw, 675px)',
            right: 'clamp(-300px, -12vw, -170px)',
            top: 'clamp(30px, 4.2vw, 100px)',
            transform: 'rotate(45deg)',
            border: 'clamp(28px, 2.6vw, 50px) solid rgba(66, 242, 247, 0.035)',
            borderRadius: 'clamp(40px, 4vw, 78px)',
            pointerEvents: 'none',
          },
          [MQ_850]: { p: '32px 24px' },
          [MQ_480]: { p: '26px 20px' },
        }}
      >
        {/* Terceira faixa opcional (spec seção 7) — precisa ser um elemento
            de verdade (só dá pra ter ::before/::after num Box só). */}
        <Box
          sx={{
            position: 'absolute',
            width: 'clamp(300px, 23.4vw, 450px)',
            height: 'clamp(300px, 23.4vw, 450px)',
            right: '10%',
            top: '16%',
            borderRadius: 'clamp(34px, 3.75vw, 72px)',
            transform: 'rotate(45deg)',
            border: 'clamp(22px, 2.2vw, 42px) solid rgba(0, 30, 39, 0.018)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            width: '100%',
            maxWidth: 430,
            [MQ_850]: { maxWidth: 420 },
          }}
        >
          {formulario}
        </Box>
      </Box>
    </Box>
  );
};
